import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  cn,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nexus/ui";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { EVENT_FILTER_ALL } from "../constants/event-filter-options";

/** Search drives a server query now, so typing must not fire one per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

export interface FilterOption {
  value: string;
  label: string;
}

interface EventsFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  statusOptions: ReadonlyArray<FilterOption>;
  /**
   * Off for a view that has no per-row status to narrow by. A control that
   * silently does nothing is the same class of lie as a duration that omits
   * the queue.
   */
  showStatusFilter?: boolean;
  topic: string;
  onTopicChange: (value: string) => void;
  topicOptions: ReadonlyArray<FilterOption>;
  actorUserId: string;
  onActorUserChange: (value: string) => void;
  userOptions: ReadonlyArray<FilterOption>;
  hidePings: boolean;
  onHidePingsChange: (value: boolean) => void;
  healthPingCount: number | undefined;
  /** Set when a bucket is selected in the activity chart; null when it is not. */
  rangeLabel: string | null;
  onClearRange: () => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  resultCount: number | null;
  /** What the count counts, so the total names the rows actually on screen. */
  resultUnit?: "events" | "actions";
  isRefreshing: boolean;
  onRefresh: () => void;
  /** Trailing actions — rendered next to refresh, ahead of it in the tab order. */
  actions?: ReactNode;
}

const CONTROL_HEIGHT = "h-8";

function FilterSelect({
  value,
  onValueChange,
  label,
  options,
}: {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  options: ReadonlyArray<FilterOption>;
}) {
  const isActive = value !== EVENT_FILTER_ALL;

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={label}
        className={cn(
          CONTROL_HEIGHT,
          "w-[150px] rounded-sm text-xs",
          isActive && "border-primary/40 bg-primary/5 text-foreground",
        )}
      >
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function EventsFilterBar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  statusOptions,
  showStatusFilter = true,
  topic,
  onTopicChange,
  topicOptions,
  actorUserId,
  onActorUserChange,
  userOptions,
  hidePings,
  onHidePingsChange,
  healthPingCount,
  rangeLabel,
  onClearRange,
  hasActiveFilters,
  onClearFilters,
  resultCount,
  resultUnit = "events",
  isRefreshing,
  onRefresh,
  actions,
}: EventsFilterBarProps) {
  const { t, i18n } = useTranslation();
  const [searchInput, setSearchInput] = useState(search);
  const { debounced: debouncedSearchChange, cancel: cancelSearchChange } =
    useDebouncedCallback(onSearchChange, SEARCH_DEBOUNCE_MS);

  // Re-sync when the committed value changes elsewhere (Clear filters, back/forward).
  useEffect(() => {
    setSearchInput(search);
    cancelSearchChange();
  }, [search, cancelSearchChange]);

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <div className="relative flex items-center">
        <Icon
          name="search"
          size="xs"
          className="pointer-events-none absolute left-2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            debouncedSearchChange(event.target.value);
          }}
          aria-label={t("admin.events.filters.searchLabel", "Search events")}
          placeholder={t(
            "admin.events.filters.searchPlaceholder",
            "Correlation ID or event type…",
          )}
          className={cn(
            CONTROL_HEIGHT,
            "w-[220px] rounded-sm pl-7 text-xs focus-visible:ring-0",
          )}
        />
      </div>

      {showStatusFilter ? (
        <FilterSelect
          value={status}
          onValueChange={onStatusChange}
          label={t("admin.events.filters.status", "All statuses")}
          options={statusOptions}
        />
      ) : null}
      <FilterSelect
        value={topic}
        onValueChange={onTopicChange}
        label={t("admin.events.filters.topic", "All topics")}
        options={topicOptions}
      />
      <FilterSelect
        value={actorUserId}
        onValueChange={onActorUserChange}
        label={t("admin.events.filters.user", "All users")}
        options={userOptions}
      />

      <label
        className={cn(
          CONTROL_HEIGHT,
          "inline-flex cursor-pointer select-none items-center gap-1.5 rounded-sm border border-input-border bg-card px-2.5 text-xs text-muted-foreground",
        )}
      >
        <Checkbox
          checked={hidePings}
          onCheckedChange={(checked) => onHidePingsChange(checked === true)}
          className="size-3.5"
        />
        {healthPingCount == null
          ? t("admin.events.filters.hidePings", "Hide health pings")
          : t("admin.events.filters.hidePingsWithCount", "Hide health pings ({{count}})", {
              count: healthPingCount,
            })}
      </label>

      {/* The chart sets this filter, so the bar is where it becomes visible and
          removable — otherwise a click on a bar looks like the list broke. */}
      {rangeLabel ? (
        <span
          className={cn(
            CONTROL_HEIGHT,
            "inline-flex items-center gap-1.5 rounded-sm border border-primary/40 bg-primary/5 pl-2 pr-1 text-xs text-foreground",
          )}
        >
          <Icon name="filter" size="xs" className="shrink-0 text-primary" aria-hidden />
          {rangeLabel}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-5 rounded-sm"
            aria-label={t("admin.events.filters.rangeClear", "Clear the selected time span")}
            onClick={onClearRange}
          >
            <Icon name="x" size="xs" aria-hidden />
          </Button>
        </span>
      ) : null}

      {hasActiveFilters ? (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-primary" onClick={onClearFilters}>
          {t("admin.events.filters.clear", "Clear filters")}
        </Button>
      ) : null}

      <span className="flex-1" />

      {resultCount == null ? null : (
        <span className="text-xs tabular-nums text-muted-foreground">
          {resultUnit === "actions"
            ? t("admin.events.filters.resultCountActions", "{{count}} actions", {
                count: resultCount,
                formattedCount: resultCount.toLocaleString(i18n.language),
              })
            : t("admin.events.filters.resultCount", "{{count}} events", {
                count: resultCount,
                formattedCount: resultCount.toLocaleString(i18n.language),
              })}
        </span>
      )}

      {actions}

      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        className="size-8 shrink-0 rounded-sm"
        aria-label={t("admin.events.filters.refresh", "Refresh list")}
        disabled={isRefreshing}
        onClick={onRefresh}
      >
        <Icon
          name="refresh-cw"
          size="xs"
          className={cn(isRefreshing && "animate-spin motion-reduce:animate-none")}
          aria-hidden
        />
      </Button>
    </div>
  );
}
